import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { cloneSiteDefaults } from "../config/siteDefaults";

const PREVIEW_SESSION_KEY = "ap:cms-preview-session-v1";
const PUBLIC_SETTINGS_CACHE_KEY = "ap:site-settings-cache-v2";
const LEGACY_PUBLIC_SETTINGS_CACHE_KEY = "ap:site-settings-cache-v1";
let memoryPublicSettings = null;

function readPublicSettingsCache() {
  if (typeof window === "undefined") return memoryPublicSettings;
  try {
    // Persist the last published settings across tabs/app launches so the
    // header/colors/hero do not first paint code defaults and then visibly
    // jump when /site-settings returns. The API still revalidates on mount.
    let raw = window.localStorage.getItem(PUBLIC_SETTINGS_CACHE_KEY);
    if (!raw) raw = window.sessionStorage.getItem(PUBLIC_SETTINGS_CACHE_KEY);
    if (!raw) raw = window.sessionStorage.getItem(LEGACY_PUBLIC_SETTINGS_CACHE_KEY);
    if (!raw) {
      memoryPublicSettings = null;
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.settings) {
      memoryPublicSettings = null;
      return null;
    }
    memoryPublicSettings = parsed;
    return parsed;
  } catch (_error) {
    return memoryPublicSettings;
  }
}

function writePublicSettingsCache(settings, version) {
  const payload = {
    settings,
    version: Number(version) || 0,
    cached_at: Date.now(),
  };
  memoryPublicSettings = payload;
  if (typeof window === "undefined") return;
  const encoded = JSON.stringify(payload);
  try {
    window.localStorage.setItem(PUBLIC_SETTINGS_CACHE_KEY, encoded);
    window.sessionStorage.removeItem(LEGACY_PUBLIC_SETTINGS_CACHE_KEY);
  } catch (_error) {
    // Fall back to session storage when persistent storage is unavailable.
    try {
      window.sessionStorage.setItem(PUBLIC_SETTINGS_CACHE_KEY, encoded);
    } catch (_sessionError) {
      // Private browsing/storage limits should never block rendering.
    }
  }
}

const SiteSettingsContext = React.createContext({
  settings: cloneSiteDefaults(),
  loading: false,
  previewMode: false,
  version: 0,
  refresh: () => {},
  exitPreview: () => {},
});

function upsertMeta(selector, attrs) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(attrs.identity || {}).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  Object.entries(attrs.values || {}).forEach(([key, value]) => {
    if (value) node.setAttribute(key, value);
    else node.removeAttribute(key);
  });
}

function pageKeyForPath(pathname) {
  if (pathname === "/") return "home";
  if (pathname === "/sell-food" || pathname === "/sell") return "sell_food";
  if (pathname === "/about") return "about";
  if (pathname === "/fees-delivery") return "fees_delivery";
  if (pathname === "/support") return "support";
  if (pathname === "/subscribe") return "subscribe";
  return null;
}

export function SiteSettingsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedPublic = React.useMemo(() => readPublicSettingsCache(), []);
  const [settings, setSettings] = React.useState(() => cachedPublic?.settings || cloneSiteDefaults());
  const [loading, setLoading] = React.useState(() => !cachedPublic);
  const [previewMode, setPreviewMode] = React.useState(false);
  const [version, setVersion] = React.useState(() => Number(cachedPublic?.version) || 0);
  const explicitPreview = React.useMemo(
    () => new URLSearchParams(location.search).get("cmsPreview") === "1",
    [location.search],
  );

  const load = React.useCallback(async () => {
    let sessionPreview = false;
    try {
      sessionPreview = window.sessionStorage.getItem(PREVIEW_SESSION_KEY) === "1";
    } catch (_storageError) {
      sessionPreview = false;
    }
    const wantsPreview = explicitPreview || sessionPreview;
    const hasCachedPublic = Boolean(readPublicSettingsCache());
    if (wantsPreview || !hasCachedPublic) setLoading(true);
    try {
      if (wantsPreview) {
        try {
          const { data } = await api.get("/admin/site-settings/preview");
          if (data?.settings) {
            let localPreview = null;
            try {
              localPreview = JSON.parse(window.sessionStorage.getItem("ap:cms-unsaved-preview-v1") || "null");
            } catch (_storageError) { /* Use the authorized saved draft if local storage fails. */ }
            setSettings(localPreview?.settings || data.settings);
            setVersion(Number(localPreview?.version ?? data.version) || 0);
            setPreviewMode(true);
            try {
              window.sessionStorage.setItem(PREVIEW_SESSION_KEY, "1");
            } catch (_storageError) {
              // Preview still works for the current route if storage is unavailable.
            }
            return;
          }
        } catch (_previewError) {
          try {
            window.sessionStorage.removeItem(PREVIEW_SESSION_KEY);
            window.sessionStorage.removeItem("ap:cms-unsaved-preview-v1");
          } catch (_storageError) {
            // Ignore storage failures; public settings still load below.
          }
          // Unauthorized/expired preview always falls back to public settings.
        }
      }
      const { data } = await api.get("/site-settings");
      const nextSettings = data?.settings || cloneSiteDefaults();
      const nextVersion = Number(data?.version) || 0;
      const cached = readPublicSettingsCache();

      // If the published version is unchanged, keep the exact cached object.
      // Replacing it with an equivalent freshly parsed object causes every
      // settings consumer (header, home hero, footer, seller landing) to
      // re-render even though nothing visible changed.
      if (!cached || Number(cached.version) !== nextVersion) {
        setSettings(nextSettings);
        setVersion(nextVersion);
      } else {
        setVersion((current) => current === nextVersion ? current : nextVersion);
      }
      writePublicSettingsCache(nextSettings, nextVersion);
      setPreviewMode(false);
    } catch (_error) {
      const cached = readPublicSettingsCache();
      if (!cached) {
        setSettings(cloneSiteDefaults());
        setVersion(0);
      }
      setPreviewMode(false);
    } finally {
      setLoading(false);
    }
  }, [explicitPreview]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useLayoutEffect(() => {
    const a = settings?.appearance || {};
    const root = document.documentElement;
    // Preserve owner-edited colors, but migrate the exact legacy defaults
    // to the current approved deeper navy / warmer border palette so already
    // published CMS settings do not pin the UI to the superseded colors.
    const migrate = (value, legacy, approved) => (
      a.palette_version !== 1 && legacy.includes(String(value || "").toLowerCase()) ? approved : value
    );
    const ink = migrate(a.text, ["#10233f", "#07182f"], "#0E2A47");
    const inkSoft = migrate(a.text_muted, ["#3f4f66", "#34445a"], "#0E2A47");
    const primary = migrate(a.primary, ["#b14b08", "#e85d04"], "#C25A2E");
    const primaryHoverDark = migrate(a.primary_hover_dark, ["#8f3905", "#c2410c"], "#C25A2E");
    const primaryHoverLight = migrate(a.primary_hover_light, ["#c45b12", "#f97316"], "#C25A2E");
    const accent = migrate(a.accent, ["#d06b1b", "#f59e0b"], "#C25A2E");
    const border = migrate(a.border, ["#e6d4bf", "#dfccb6"], "#F1E4D6");
    const vars = {
      "--ap-ink": ink,
      "--ap-ink-soft": inkSoft,
      "--ap-primary": primary,
      "--ap-primary-hover-dark": primaryHoverDark || primary,
      "--ap-primary-hover-light": primaryHoverLight || primary,
      "--ap-accent": accent,
      "--ap-border": border,
    };
    Object.entries(vars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
    });
  }, [settings]);

  React.useEffect(() => {
    const key = pageKeyForPath(location.pathname);
    const seo = key ? settings?.seo?.pages?.[key] : null;
    if (!seo) {
      document.title = "Arctic Plate";
      upsertMeta('meta[name="description"]', {
        identity: { name: "description" },
        values: { content: "" },
      });
      return;
    }
    document.title = seo.title || "Arctic Plate";
    upsertMeta('meta[name="description"]', {
      identity: { name: "description" },
      values: { content: seo.description || "" },
    });
  }, [location.pathname, settings]);

  const exitPreview = React.useCallback(async () => {
    try {
      window.sessionStorage.removeItem(PREVIEW_SESSION_KEY);
      window.sessionStorage.removeItem("ap:cms-unsaved-preview-v1");
    } catch (_storageError) {
      // Continue with explicit public reload below.
    }

    setLoading(true);
    try {
      const { data } = await api.get("/site-settings");
      const nextSettings = data?.settings || cloneSiteDefaults();
      const nextVersion = Number(data?.version) || 0;
      setSettings(nextSettings);
      setVersion(nextVersion);
      writePublicSettingsCache(nextSettings, nextVersion);
    } catch (_error) {
      setSettings(cloneSiteDefaults());
      setVersion(0);
    } finally {
      setPreviewMode(false);
      setLoading(false);
    }

    const params = new URLSearchParams(location.search);
    params.delete("cmsPreview");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const value = React.useMemo(
    () => ({ settings, loading, previewMode, version, refresh: load, exitPreview }),
    [settings, loading, previewMode, version, load, exitPreview],
  );

  return (
    <SiteSettingsContext.Provider value={value}>
      {previewMode && (
        <div
          data-testid="cms-preview-banner"
          className="fixed left-1/2 top-3 z-[100] flex -translate-x-1/2 items-center gap-3 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-900 shadow-lg"
        >
          <span>Owner preview — unpublished changes</span>
          <button
            type="button"
            onClick={exitPreview}
            className="rounded-full border border-amber-400 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wide"
          >
            Exit preview
          </button>
        </div>
      )}
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return React.useContext(SiteSettingsContext);
}

